import { extendObservable } from "mobx";

import { AuthService, IStorage } from "@formant/auth-lib";
import { AuthClient } from "@formant/core/client/AuthClient";
import { OrganizationClient } from "@formant/core/client/OrganizationClient";
import { RoleClient } from "@formant/core/client/RoleClient";
import { ShareClient } from "@formant/core/client/ShareClient";
import { UserClient } from "@formant/core/client/UserClient";
import { defined } from "@formant/core/common/defined";
import { setMetadata } from "@formant/core/common/setMetadata";
import {
    hasHighEnoughRole,
    UserRoleType,
} from "@formant/core/model/AccessLevel";
import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { duration } from "@formant/core/model/duration";
import { Feature } from "@formant/core/model/Feature";
import { features as allFeatures } from "@formant/core/model/features";
import { ILoginResult } from "@formant/core/model/ILoginResult";
import { IOrganization } from "@formant/core/model/IOrganization";
import { includesAccess, ResourcePolicy } from "@formant/core/model/Policies";
import { Resource } from "@formant/core/model/Resource";
import { Uuid } from "@formant/core/model/Uuid";

import { identify as identifyForAnalytics } from "../analytics/identify";
import { trialBannerDismissedFlag } from "../component/TrialBannerDismissedFlag";
import { IAuthStore } from "./IAuthStore";
import { shareTeleopDeviceIdKey } from "./ShareTeleopDeviceIdKey";
import { StoreCache } from "./StoreCache";

export class AuthStore extends AuthService implements IAuthStore {
    private featuresCache = new StoreCache<Uuid, Set<Feature>>({
        staleIntervalMs: 5 * duration.minute,
    });

    constructor(
        authClient: AuthClient,
        private organizationClient: OrganizationClient,
        roleClient: RoleClient,
        shareClient: ShareClient,
        userClient: UserClient,
        storage?: IStorage,
    ) {
        super(
            authClient as any,
            {} as any,
            roleClient as any,
            shareClient as any,
            userClient as any,
            storage,
        );
        // Explicitly make all properties observable since parent class also has these properties
        // and @observable decorators don't work properly when shadowing parent properties
        extendObservable(this, {
            userId: undefined,
            userRole: undefined,
            userTags: undefined,
            organizationId: undefined,
            challenge: undefined,
            isSuperuser: false,
            currentToken: undefined,
            refreshToken: undefined,
        });
        this.initialize();
    }

    reauthenticate() {
        super.reauthenticate();
        localStorage.removeItem(shareTeleopDeviceIdKey);
    }

    getFeatures(): Set<Feature> | undefined {
        const { organizationId, authenticated, currentToken } = this;
        const devlopmentOnly: Set<Feature> = new Set();

        if (authenticated && currentToken && organizationId) {
            return this.featuresCache.get(
                organizationId,
                async () =>
                    new Set([
                        ...devlopmentOnly,
                        ...(await this.authClient.getFeatures(currentToken)),
                    ]),
            );
        }
    }

    async fetchFeatures() {
        const { authenticated, currentToken } = this;
        return authenticated
            ? await this.authClient.getFeatures(defined(currentToken))
            : [];
    }

    featureEnabled(feature: Feature): boolean {
        const features = this.getFeatures();
        return features?.has(feature) === true;
    }

    hasAccess<T extends Resource = Resource>(
        resource: T,
        requiredAccess?: ResourcePolicy<T>,
        requiredFeature?: Feature,
    ): boolean {
        return (
            this.hasRequiredAccess(resource, requiredAccess || "viewer") &&
            (requiredFeature ? this.featureEnabled(requiredFeature) : true) &&
            (allFeatures.includes(resource as Feature)
                ? this.featureEnabled(resource as Feature)
                : true)
        );
    }

    hasAccessByRole(role: UserRoleType): boolean {
        const { userRole } = this;
        if (!userRole) {
            return false;
        }
        return hasHighEnoughRole(userRole.name as UserRoleType, role);
    }

    hasRequiredAccess<T extends Resource = Resource>(
        resource: T,
        requiredAccess: ResourcePolicy<T>,
    ): boolean {
        return (
            !!this.userRole?.policies &&
            includesAccess(this.userRole.policies[resource], requiredAccess)
        );
    }

    private getOrganization(
        token: string,
        id: string,
    ): Promise<DefinedEntity<IOrganization> | undefined> {
        return this.organizationClient.get(token, id);
    }

    protected async doLogin(credentials: ILoginResult): Promise<ILoginResult> {
        await super.doLogin(credentials);
        localStorage.setItem(trialBannerDismissedFlag, "false");
        return credentials;
    }

    protected async handleLoginResult(
        loginResult: ILoginResult,
        termsAccepted?: string,
    ) {
        await super.handleLoginResult(loginResult, termsAccepted);

        if (
            !this.organizationId ||
            !this.userId ||
            !this.currentToken ||
            !this.user
        ) {
            return;
        }

        setMetadata("organizationId", this.organizationId);
        setMetadata("userId", this.userId);

        const organization = defined(
            await this.getOrganization(this.currentToken, this.organizationId),
        );
        identifyForAnalytics(this.user, organization);
    }
}
